/* ═══════════════════════════════════════════════════════════════════════════
   HERO IMAGES — device-aware filtering & detection
   ─────────────────────────────────────────────────
   Centralises:
     • The schema for hero background images (each can be tagged "all" /
       "desktop" / "mobile" to control where it appears).
     • Backward-compatible reading of the legacy schema where the gallery
       was a string[] and the primary image was a bare URL — those still
       work, and any unspecified entry defaults to `devices: 'all'`.
     • The viewport-class predicate (`isMobileViewport()`) so Hero.js,
       Home.js, and any future consumer all draw the same line.

   Both consumers (Hero.js for the image rotation, Home.js for the
   destinations-section "force white" styling) call `getActiveHeroImages()`
   to get the list of URLs that should render at the current viewport size.
   If that list is empty, picture-driven affordances like "force white
   text" must be disabled — that's the second part of the user-requested
   fix ("force white text should only work on picture background").
   ═══════════════════════════════════════════════════════════════════════════ */

export const DEVICE_OPTIONS = [
    { value: 'all',     label: 'All devices' },
    { value: 'desktop', label: 'Desktop only' },
    { value: 'mobile',  label: 'Mobile only' },
];

/** True when the viewport is in mobile mode (<= 767px). SSR-safe. */
export const isMobileViewport = () =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;

/** Normalize one entry from any of:
 *    'https://…'                     (legacy string)
 *    { url: 'https://…' }             (object without device)
 *    { url: 'https://…', devices: 'mobile' }  (full object)
 *  Returns { url, devices } with sensible defaults. */
const normalizeEntry = (entry) => {
    if (!entry) return null;
    if (typeof entry === 'string') {
        const url = entry.trim();
        return url ? { url, devices: 'all' } : null;
    }
    if (typeof entry === 'object') {
        const url = String(entry.url || '').trim();
        if (!url) return null;
        const devices = entry.devices === 'desktop' || entry.devices === 'mobile' ? entry.devices : 'all';
        return { url, devices };
    }
    return null;
};

/** All hero-image entries (primary + gallery) as normalized {url,devices}
 *  objects, in display order, with empties dropped and duplicates kept
 *  only on first occurrence. Does NOT filter by viewport — `getActiveHeroImages`
 *  layers that on top. Useful when you need to inspect everything (admin UI). */
export const getAllHeroEntries = (cfg) => {
    const seen = new Set();
    const out = [];
    const push = (entry) => {
        const n = normalizeEntry(entry);
        if (!n) return;
        if (seen.has(n.url)) return;
        seen.add(n.url);
        out.push(n);
    };
    // Primary image — note: legacy data has it as `bgImage` (string) +
    // optional `bgImageDevices`; new schema is the same string + devices.
    if (cfg?.bgImage) {
        push({ url: cfg.bgImage, devices: cfg.bgImageDevices });
    }
    // Gallery — array of strings (legacy) OR objects (new).
    if (Array.isArray(cfg?.bgImages)) {
        cfg.bgImages.forEach(push);
    }
    return out;
};

/** Hero image URLs that should display at the given viewport class.
 *  Pass `isMobile=true` for phone mode, `false` for desktop mode. */
export const getActiveHeroImages = (cfg, isMobile) => {
    const currentDevice = isMobile ? 'mobile' : 'desktop';
    return getAllHeroEntries(cfg)
        .filter(({ devices }) => devices === 'all' || devices === currentDevice)
        .map(({ url }) => url);
};

/** Convenience: any picture background at all for the current viewport? */
export const hasActiveHeroImages = (cfg, isMobile) =>
    getActiveHeroImages(cfg, isMobile).length > 0;
